import { useEffect, useRef, useState } from "react";

const MAX_IDENTIFIER_LENGTH = 128;
import { Box, Grid } from "@mui/material";
import {
    StriimFeedbackBlock,
    StriimInputField,
    StriimTypography,
    StriimTabs,
    StriimTab,
    StriimButtonGroup,
    StriimChip,
    StriimTooltip
} from "@striim/striim-ui";
import { Controlled as CodeMirror } from "react-codemirror2";
//@ts-ignore
import "codemirror/lib/codemirror.css";
import "codemirror/mode/sql/sql";
import { Close, Checkmark, InfoCircle } from "src/generic/icon/customIcons";
import WarningTriangle from "src/generic/icon/wizard-icon/warning.svg";
import LoadingIndicator from "src/generic/loading-indicator";
import { styles } from "./add-queries-modal.styles";
import QueryPreview, { QueryPreviewState } from "../query-preview/query-preview";
import { QueryItem } from "./add-queries-modal";
import { isPreviewOutdated } from "./add-queries-modal.utils";
import { isValidIdentifier } from "../../select-tables-modal/select-tables.utils";

interface QueryEditorProps {
    selectedQuery: QueryItem;
    queryErrors: Record<string, string>;
    dismissedErrors: Set<string>;
    duplicateIdentifiers: Set<string>;
    isValidating: boolean;
    emptyQueryErrorType: "save" | "add" | null;
    adapterName: string;
    databaseProviderType?: string;
    previewState: QueryPreviewState | undefined;
    onUpdateQuery: (id: string, field: keyof QueryItem, value: string | number) => void;
    onSqlChange: (value: string) => void;
    onValidateQuery: () => void;
    onPreviewQuery: () => void;
    onToggleDismissError: (queryId: string, dismissed: boolean) => void;
    onDismissEmptyQueryError: () => void;
}

const QueryEditor: React.FC<QueryEditorProps> = ({
    selectedQuery,
    queryErrors,
    dismissedErrors,
    duplicateIdentifiers,
    isValidating,
    emptyQueryErrorType,
    adapterName,
    databaseProviderType,
    previewState,
    onUpdateQuery,
    onSqlChange,
    onValidateQuery,
    onPreviewQuery,
    onToggleDismissError,
    onDismissEmptyQueryError
}) => {
    const [tabIndex, setTabIndex] = useState(0);
    const editorRef = useRef<any>(null);

    const isQueryEmpty = !selectedQuery.sql?.trim();
    const hasPreviewForCurrent = !!previewState;
    const previewOutdated = isPreviewOutdated({ state: previewState, currentSql: selectedQuery.sql });
    const isPreviewLoading = previewState?.isLoading ?? false;
    const isPreviewDisabled = isQueryEmpty || isPreviewLoading;

    useEffect(() => {
        if (isQueryEmpty && !hasPreviewForCurrent && tabIndex === 1) {
            setTabIndex(0);
        }
    }, [isQueryEmpty, hasPreviewForCurrent, tabIndex]);

    useEffect(() => {
        setTabIndex(0);
        if (editorRef.current) {
            editorRef.current.focus();
        }
    }, [selectedQuery.id]);

    const selectedQueryError = queryErrors[selectedQuery.id];
    const isSelectedErrorDismissed = dismissedErrors.has(selectedQuery.id);
    const selectedErrorCount = selectedQueryError ? 1 : 0;
    const selectedHasDuplicate = duplicateIdentifiers.has(selectedQuery.identifier);
    const selectedHasEmptyIdentifier = !selectedQuery.identifier?.trim();
    const selectedHasInvalidIdentifier = !isValidIdentifier(selectedQuery.identifier);
    const selectedHasTooLongIdentifier = (selectedQuery.identifier?.length ?? 0) > MAX_IDENTIFIER_LENGTH;

    const handlePreview = () => {
        if (isPreviewDisabled) return;
        onPreviewQuery();
        setTabIndex(1);
    };

    return (
        <Grid item sx={styles.rightSection}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                    <StriimInputField
                        label="Query Identifier"
                        value={selectedQuery.identifier}
                        onChange={(value: string) => {
                            onUpdateQuery(selectedQuery.id, "identifier", value?.toUpperCase());
                        }}
                        fullWidth
                        error={
                            selectedHasDuplicate ||
                            selectedHasEmptyIdentifier ||
                            selectedHasInvalidIdentifier ||
                            selectedHasTooLongIdentifier
                        }
                        helperText={
                            selectedHasEmptyIdentifier
                                ? "Query Identifier is required"
                                : selectedHasTooLongIdentifier
                                ? `Value cannot be greater than ${MAX_IDENTIFIER_LENGTH} characters`
                                : selectedHasInvalidIdentifier
                                ? "Please enter an identifier with only alphanumeric or underscore characters."
                                : selectedHasDuplicate
                                ? "This name is already used"
                                : "Label queries to identify and reference them uniquely"
                        }
                        InputProps={{ "data-testid": "query-identifier-input" }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <StriimInputField
                        label="Execution Priority"
                        value={selectedQuery.priority}
                        fullWidth
                        type="number"
                        disabled
                        helperText="Queries will execute in ascending order (1, 2, 3...)"
                        InputProps={{ "data-testid": "query-execution-order-input" }}
                    />
                </Grid>
                <Grid item xs={3} display="flex" justifyContent="flex-end" data-test-id="query-actions">
                    <StriimButtonGroup
                        label="default"
                        buttonHelper={{
                            default: {
                                primaryBtnlabel: "Preview Query",
                                primayBtnOnClick: handlePreview,
                                primaryBtnToolTip: isQueryEmpty
                                    ? "Enter a query to preview"
                                    : isPreviewLoading
                                    ? "Preview is loading"
                                    : "Preview Query",
                                primaryBtnStyles: {
                                    minWidth: "133px",
                                    ...(isPreviewDisabled ? styles.disabledPrimaryBtn : {})
                                },
                                dropdownListStyle: {
                                    zIndex: 1301,
                                    "& > .MuiPaper-root": {
                                        width: "175px"
                                    }
                                },
                                dropdownList: [
                                    {
                                        title: "Validate Only",
                                        onClick: onValidateQuery,
                                        disabled: isValidating || isQueryEmpty
                                    }
                                ]
                            }
                        }}
                    />
                </Grid>
            </Grid>
            <Box sx={styles.headerDivider} />
            <Box height="460px" display="flex" flexDirection="column" sx={styles.tabsWrapper}>
                <StriimTooltip
                    componentsProps={{ tooltip: { sx: styles.guidelinesTooltipPaper } }}
                    title={
                        <Box sx={styles.guidelinesTooltipContent}>
                            <Box sx={styles.guidelinesTooltipRow}>
                                <Box component="span" sx={styles.guidelinesTooltipCheckIcon}>
                                    <Checkmark />
                                </Box>
                                <StriimTypography variant="caption3" fontFamily={"Inter"} color="additional.white">
                                    Use unquoted identifiers: SCHEMA.TABLE, not "SCHEMA"."TABLE"
                                </StriimTypography>
                            </Box>
                            <Box sx={styles.guidelinesTooltipRow}>
                                <Box component="span" sx={styles.guidelinesTooltipCheckIcon}>
                                    <Checkmark />
                                </Box>
                                <StriimTypography variant="caption3" fontFamily={"Inter"} color="additional.white">
                                    Use aliases for duplicate column names in JOINs
                                </StriimTypography>
                            </Box>
                        </Box>
                    }
                    placement="bottom-end"
                >
                    <Box sx={styles.guidelinesLink}>
                        <Box component="span" sx={styles.guidelinesLinkIcon}>
                            <InfoCircle />
                        </Box>
                        <StriimTypography variant="caption3" fontFamily={"Inter"} color="secondary.500">
                            Query Format Guidelines
                        </StriimTypography>
                    </Box>
                </StriimTooltip>
                <StriimTabs
                    value={tabIndex}
                    onChange={(_e, newValue) => setTabIndex(newValue)}
                    contentStyles={{ overflow: "scroll", height: "100%" }}
                    content={
                        <>
                            {tabIndex === 0 ? (
                                <Box
                                    sx={styles.queryEditorContainer}
                                    onClick={e => {
                                        const cm = (e.currentTarget as HTMLElement).querySelector(".CodeMirror") as any;
                                        cm?.CodeMirror?.focus();
                                    }}
                                    data-test-id="query-input"
                                >
                                    <CodeMirror
                                        value={selectedQuery.sql}
                                        options={{
                                            mode: "text/x-sql",
                                            lineWrapping: true
                                        }}
                                        editorDidMount={editor => {
                                            editorRef.current = editor;
                                            editor.focus();
                                        }}
                                        onBeforeChange={(_editor, _data, value) => {
                                            onSqlChange(value);
                                        }}
                                    />
                                </Box>
                            ) : (
                                <QueryPreview
                                    query={selectedQuery}
                                    state={previewState}
                                    adapterName={adapterName}
                                    databaseProviderType={databaseProviderType}
                                />
                            )}
                        </>
                    }
                >
                    <StriimTab label="Query" />
                    <StriimTab
                        label={
                            (
                                <Box component="span" display={"flex"} sx={styles.tabLabelWithIcon}>
                                    {previewOutdated && hasPreviewForCurrent && (
                                        <Box
                                            component="span"
                                            display={"flex"}
                                            alignItems={"center"}
                                            width="100%"
                                            sx={styles.tabWarningIcon}
                                        >
                                            <WarningTriangle />
                                        </Box>
                                    )}
                                    Preview
                                </Box>
                            ) as any
                        }
                        disabled={(!isQueryEmpty && !hasPreviewForCurrent) || (isQueryEmpty && !hasPreviewForCurrent)}
                    />
                </StriimTabs>
            </Box>

            {selectedQueryError && !isSelectedErrorDismissed && (
                <Box sx={styles.validationFeedback}>
                    <StriimFeedbackBlock
                        variant="critical"
                        content={
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
                                <Box display="flex" flexDirection="column">
                                    <StriimTypography variant="h3" color="greyscale.900">
                                        Query Validation failed
                                    </StriimTypography>
                                    <StriimTypography variant="body4" color="greyscale.900">
                                        {selectedQueryError}
                                    </StriimTypography>
                                </Box>
                                <Box
                                    sx={{ cursor: "pointer", display: "flex", flexShrink: 0 }}
                                    onClick={() => onToggleDismissError(selectedQuery.id, true)}
                                >
                                    <Close sx={{ fontSize: 18, color: "greyscale.700" }} />
                                </Box>
                            </Box>
                        }
                    />
                </Box>
            )}

            {isValidating ? (
                <Box>
                    <StriimTypography
                        variant="caption3"
                        color={"greyscale.900"}
                        fontFamily={"Inter"}
                        data-test-id="validating-query-info"
                    >
                        Validating Query
                    </StriimTypography>
                    <LoadingIndicator isGlobal={false} variant="indeterminate" sx={styles.validateProgressBar} />
                </Box>
            ) : selectedQueryError && isSelectedErrorDismissed ? (
                <Box sx={styles.errorChipContainer}>
                    <StriimChip
                        type="tag-chip"
                        label={`${selectedErrorCount} ERROR${selectedErrorCount > 1 ? "S" : ""}`}
                        variant="error"
                        sx={styles.errorChip}
                        onClick={() => onToggleDismissError(selectedQuery.id, false)}
                    />
                </Box>
            ) : null}
            {emptyQueryErrorType && (
                <Grid item>
                    <StriimFeedbackBlock
                        variant="critical"
                        content={
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
                                <Box display="flex" flexDirection="column">
                                    <StriimTypography variant="h3" color="greyscale.900">
                                        Empty query detected
                                    </StriimTypography>
                                    <StriimTypography variant="body4" color="greyscale.900">
                                        {emptyQueryErrorType === "save"
                                            ? "There's an empty query. Fill in the query or delete it before saving."
                                            : "There's an empty query in progress. Complete it before adding a new one."}
                                    </StriimTypography>
                                </Box>
                                <Box
                                    sx={{ cursor: "pointer", display: "flex", flexShrink: 0 }}
                                    onClick={onDismissEmptyQueryError}
                                >
                                    <Close />
                                </Box>
                            </Box>
                        }
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default QueryEditor;
